import type { ConflictRecord, OfflineDraft, OfflineMediaRecord, OutboxRequest } from "@/types/offline";

const DB_NAME = "aczon-arise-field-v1";
const DB_VERSION = 2;
export const stores = { drafts: "drafts", outbox: "outbox", mediaOutbox: "mediaOutbox", conflicts: "conflicts", meta: "meta" } as const;

type StoreName = typeof stores[keyof typeof stores];

const openDatabase = (): Promise<IDBDatabase> => new Promise((resolve, reject) => {
  const request = indexedDB.open(DB_NAME, DB_VERSION);
  request.onupgradeneeded = () => {
    const database = request.result;
    if (!database.objectStoreNames.contains(stores.drafts)) database.createObjectStore(stores.drafts, { keyPath: "id" });
    if (!database.objectStoreNames.contains(stores.outbox)) database.createObjectStore(stores.outbox, { keyPath: "id" });
    if (!database.objectStoreNames.contains(stores.mediaOutbox)) database.createObjectStore(stores.mediaOutbox, { keyPath: "id" });
    if (!database.objectStoreNames.contains(stores.conflicts)) database.createObjectStore(stores.conflicts, { keyPath: "id" });
    if (!database.objectStoreNames.contains(stores.meta)) database.createObjectStore(stores.meta, { keyPath: "key" });
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error || new Error("Unable to open offline database"));
});

const transaction = async <T>(storeName: StoreName, mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = database.transaction(storeName, mode);
    const request = action(tx.objectStore(storeName));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error(`Offline ${storeName} operation failed`));
    tx.oncomplete = () => database.close();
    tx.onerror = () => reject(tx.error || new Error(`Offline ${storeName} transaction failed`));
  });
};

export const putRecord = async <T>(storeName: StoreName, record: T): Promise<T> => { await transaction(storeName, "readwrite", (store) => store.put(record) as IDBRequest<IDBValidKey>); return record; };
export const getRecord = async <T>(storeName: StoreName, id: string): Promise<T | undefined> => transaction(storeName, "readonly", (store) => store.get(id));
export const getAllRecords = async <T>(storeName: StoreName): Promise<T[]> => transaction(storeName, "readonly", (store) => store.getAll());
export const deleteRecord = async (storeName: StoreName, id: string): Promise<void> => { await transaction(storeName, "readwrite", (store) => store.delete(id)); };

export const completeOutboxRecord = async <T>(draft: OfflineDraft<T>, outboxId: string): Promise<void> => {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = database.transaction([stores.drafts, stores.outbox], "readwrite");
    tx.objectStore(stores.drafts).put(draft);
    tx.objectStore(stores.outbox).delete(outboxId);
    tx.oncomplete = () => { database.close(); resolve(); };
    tx.onerror = () => { database.close(); reject(tx.error || new Error("Offline sync completion transaction failed")); };
    tx.onabort = () => { database.close(); reject(tx.error || new Error("Offline sync completion transaction was aborted")); };
  });
};


export const getDeviceId = (): string => {
  const key = "aczon_arise_device_id";
  const current = localStorage.getItem(key);
  if (current) return current;
  const created = crypto.randomUUID();
  localStorage.setItem(key, created);
  return created;
};

export const offlineDb = {
  saveDraft: <T>(record: OfflineDraft<T>) => putRecord<OfflineDraft<T>>(stores.drafts, record),
  getDraft: <T = unknown>(id: string) => getRecord<OfflineDraft<T>>(stores.drafts, id),
  listDrafts: () => getAllRecords<OfflineDraft>(stores.drafts),
  saveOutbox: <T>(record: OutboxRequest<T>) => putRecord<OutboxRequest<T>>(stores.outbox, record),
  getOutbox: <T = unknown>(id: string) => getRecord<OutboxRequest<T>>(stores.outbox, id),
  listOutbox: () => getAllRecords<OutboxRequest>(stores.outbox),
  removeOutbox: (id: string) => deleteRecord(stores.outbox, id),
  saveMediaOutbox: (record: OfflineMediaRecord) => putRecord<OfflineMediaRecord>(stores.mediaOutbox, record),
  getMediaOutbox: (id: string) => getRecord<OfflineMediaRecord>(stores.mediaOutbox, id),
  listMediaOutbox: () => getAllRecords<OfflineMediaRecord>(stores.mediaOutbox),
  removeMediaOutbox: (id: string) => deleteRecord(stores.mediaOutbox, id),
  saveConflict: <T>(record: ConflictRecord<T>) => putRecord<ConflictRecord<T>>(stores.conflicts, record),
  getConflict: <T = unknown>(id: string) => getRecord<ConflictRecord<T>>(stores.conflicts, id),
  listConflicts: () => getAllRecords<ConflictRecord>(stores.conflicts),
};
